import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { Blade, Button } from 'components'
import { InputFormField } from "components/hookForm"
import { updateBulkUpdateGZC } from 'api'
import { Container, Toast as toast } from 'components'
import { reportException } from 'tools/logs';
import { useForm, FormProvider} from 'react-hook-form'
import { validateGZCByCountry } from 'tools/validators'
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material'
import strings from 'strings'
import { SUPPORTED_COUNTRIES, DEFAULT_COUNTRY, FORM_FIELDS } from 'constants/garagingZipcodeConfig'

export interface ApiRefBulkGZC { setListIndexGZC: (params: any) => void }
interface ApiProps { handleSaveSon: () => void}
interface ApiRowProps{default: any}

interface InputManualGZC { 
  getValue: () => any;
  validateForm?: () => Promise<boolean>;  
}

const InputBulk = forwardRef<InputManualGZC,ApiRowProps>((props, ref) => {
  const getValue = () => {
    return {
      manualGzc: getValues(FORM_FIELDS.MANUAL_GZC),
      country: getValues(FORM_FIELDS.COUNTRY)
    }
  }

  const methods = useForm<any>({
    reValidateMode: 'onChange',
    mode: 'all',
    defaultValues: {
      [FORM_FIELDS.MANUAL_GZC]: props.default,
      [FORM_FIELDS.COUNTRY]: DEFAULT_COUNTRY
    }
  })
  const { getValues, setValue, watch } = methods
  const countryValue = watch(FORM_FIELDS.COUNTRY)
 
  useImperativeHandle(ref, () => ({ 
    getValue,
    validateForm: () => methods.trigger(FORM_FIELDS.MANUAL_GZC)
  }))

  return (<div style={{ 'display': 'flex', 'flexDirection': 'column', 'gap': '1rem' }}>
    <p className='text-sm text-muted-foreground' style={{ 'color': '#757575' }}>
      {strings.MANUAL_GZC.BULK_EDIT_DESCRIPTION}
    </p>
    <FormProvider {...methods}>
      <FormControl fullWidth style={{ marginTop: '0.5rem' }}>
        <InputLabel id="country-label">{strings.MANUAL_GZC.COUNTRY_LABEL}</InputLabel>
        <Select
          labelId="country-label"
          id="country-select"
          value={countryValue}
          label={strings.MANUAL_GZC.COUNTRY_LABEL}
          onChange={(e) => setValue(FORM_FIELDS.COUNTRY, e.target.value)}
        >
          {SUPPORTED_COUNTRIES.map((country) => (
            <MenuItem key={country.code} value={country.code}>
              {country.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <InputFormField
        id="manualGZC_input"
        name={FORM_FIELDS.MANUAL_GZC}
        label={strings.MANUAL_GZC.LABEL}
        validate={(value: string) => validateGZCByCountry(value, countryValue)}
        className="border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </FormProvider>
  </div>
  )
})

const BulkEditManualGZC = React.forwardRef<ApiRefBulkGZC, ApiProps>((props, ref) => {
  const [manualGZPDefault, setManualGZPDefault] = useState('')
  const [hasChecked, setHasChecked] = useState(false)
  const [loading, setLoading] = useState(false)
  const [disabledOpenBladeGZC, setDisabledOpenBladeGZC] = useState({
    disabledOpenBladeGZC: true,
    listIndexGZC: []
  })

  const inputRef = useRef<InputManualGZC>(null)

  const handleCloseEditBulkManualGZC = () => {
    setHasChecked(false)
  }

  const handleSubmitBulkManualGZC = async () => {
    // Validate the form first
    const isValid = await inputRef.current?.validateForm?.();

    if (!isValid) {
      return; // Stop submission if validation fails
    }else{    
    const formValues = inputRef.current?.getValue()
    const payload = {
      listAssetEldId: disabledOpenBladeGZC.listIndexGZC ? disabledOpenBladeGZC.listIndexGZC : null,
      manualGzc: formValues?.manualGzc || null,
      country: formValues?.country || DEFAULT_COUNTRY
    }
    setManualGZPDefault(formValues?.manualGzc || '')
    setLoading(true)

    updateBulkUpdateGZC(
      payload,
      localStorage.getItem('carrierId') ?? '',
      localStorage.getItem('carrierCodeType') ?? '',
      localStorage.getItem('carrierCodeValue') ?? ''
    )
      .then((res: any) => {
        toast({ id: 'updateBulkGzc', type: 'success', subTitle: res?.message })
        // Only close the blade after successful response
        handleCloseEditBulkManualGZC()
        // Update parent with minimal refreshing
        setTimeout(() => {
          props.handleSaveSon()
        }, 100) // Short delay to ensure DOM is ready
      })
      .catch((error: any) => {
        reportException(error)
        setLoading(false)
      })
    }
  }

  const handleOpenModalBulkGZC = () => {
    if (disabledOpenBladeGZC.listIndexGZC.length > 0) {
      setHasChecked(true)
    }
  }

  const setListIndexGZC = (index: any) => {
    setDisabledOpenBladeGZC({
      disabledOpenBladeGZC: index.length === 0,
      listIndexGZC: index
    })
  }

  useImperativeHandle(ref, () => ({
    setListIndexGZC
  }))

  return (<>
    <Container>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1em' }}>
        <Button
          disabled={disabledOpenBladeGZC.disabledOpenBladeGZC}
          variant="contained"
          color="primary"
          label={strings.MANUAL_GZC.BULK_EDIT_BUTTON}
          onClick={handleOpenModalBulkGZC}
        />
      </div>
    </Container> 

    <Blade
      open={hasChecked}
      title={strings.MANUAL_GZC.BULK_EDIT_TITLE}
      onClose={handleCloseEditBulkManualGZC}
      bodyNode={<InputBulk ref={inputRef} default={manualGZPDefault}></InputBulk>}
      footerBtns={{
        primary: {
          id: "createOrUpdateBulkManualGZC",
          label: 'Save',
          onClick: handleSubmitBulkManualGZC,
          disabled: false,
          loading: loading
        },
        secondary: {
          id: "closeBulkManualGZC",
          label: 'Close',
          variant: 'outlined',
          onClick: handleCloseEditBulkManualGZC,
        },
      }}
    />
  </>
  )
})

export default BulkEditManualGZC 
